import * as Moment from "moment";
import Enumerable from "linq";

import {
  Point,
  Size,
  Rectangle,
} from "../../../domain/drawing";

import {
  IItem,
  AbstractItem,
  instanceOfISourceEditable,
  instanceOfIClickChainApplicable,
  instanceOfIClickChainRequestable,
} from "../../../domain/document/items";

import {
  ITypeDefinable,
} from "./beans/items";

import {
  LayerImageSource,
  TranslatorRepository,
} from ".";

import {
  IPatientBean,
} from "../../patient/ver2/beans";

import {
  IDepartmentBean,
} from "../../department/ver2/beans";

import {
  ISectionBean,
  IDocumentBean,
  IPatientProfileInformationBean,
  IDocumentTokenInformationBean,
  ICurrentEditorBean,
  INewDocumentRequestBean,
  IDocumentButtonControl,
  IDocumentObjectsInformationBean,
  IDocumentObjectsInformationSectionBean,
  IDocumentObjectsInformationObjectBean,
  IDocumentObjectsInformationObjectOptionBean,
  INewDocumentAlternativeRequestBean,
  IDocumentSearchPresetConditionBean,
  IDocumentSearchParameterBean,
  IDocumentSearchPresetConditionOrderBean,
  IDocumentPublishedBean,
  IDocumentEditToolControl,
  IDocumentListPresetSortConditionBean,
  IDocumentListPresetSortConditionItemItemBean,
  IExternalCommentBean,
  IDocumentCollaborationSupplementBean,
  IDocumentCollaborationBehaviorBean,
  IDocumentInjectedDataBean,
  IDocumentInjectedObjectBean,
  IDocumentInjectedGroundBean,
  IDocumentInjectedEvidenceBean,
  IDocumentInjectedEvidenceAuthorBean,
} from "./beans";

import {
  Document,
  Section,
  ItemSet,
  DocumentAttributes,
  ReferenceFileInformation,
  DocumentTokenInformation,
  CurrentEditors,
  RequestedObject,
  DocumentButtonControl,
  DocumentObjectsInformation,
  DocumentObjectsInformationSection,
  DocumentObjectsInformationObject,
  DocumentObjectsInformationObjectOption,
  IBookmark,
  RelationalActionKinds,
  ChainedDocumentIdentifier,
  INewDocumentRequest,
  INewDocumentAlternativeRequest,
  DocumentSearchPresetCondition,
  DocumentSearchParameter,
  IDocumentPublished,
  DocumentEditToolControl,
  DocumentListPresetSortCondition,
  DocumentListPresetSortConditionItem,
  ExternalComment,
  DocumentCollaborationSupplement,
  DocumentParameterContainer,
  DocumentCollaborationBehavior,
  DocumentEditOperation,
  DocumentInjectedData,
  DocumentInjectedObject,
  DocumentInjectedGround,
  DocumentInjectedEvidence,
  DocumentInjectedContentType,
  DocumentInjectedEvidenceText,
  DocumentInjectedEvidenceAuthor,
  DocumentInjectedEvidenceHtml,
  DocumentInjectedEvidenceUrl,
} from "../../../domain/document";

import {
  Patient,
  Sex,
  PatientProfileInformation,
} from "../../../domain/patient";

import {
  Department,
} from "../../../domain/department";

import {
  ObjectTranslator as FormatObjectTranslator,
} from "../../format/ver2";
import {
  IFormatBean,
} from "../../format/ver2/beans";
import { FormatReference, FormatIdentifier } from "../../../domain/format";
import { HisInformationTranslator } from "../../his/ver2/HisInformationTranslator";
import { IHisInformationBean } from "../../his/ver2/beans";
import { HisInformation } from "../../../domain/his";
import { TimeMeasure } from "../../../utilities";
import { EditRuleSetTranslator, PatientProfileInformationTranslator } from "./translators";
import { DesignatedDoctorRepository } from "../../user/ver2";
import { IReferenceFileInformationBean } from "./beans/IReferenceFileInformationBean";
import { ItemsAlternator } from "./ItemsAlternator";
import { SectionThumbnailImageSource } from "./SectionThumbnailImageSource";
import { InstitutionItem } from "../../../domain/institution";
import { IInstitutionBean } from "../../institution/ver2/beans";
import { InstitutionInformationTranslator } from "../../institution/ver2/InstitutionInformationTranslator";
import { User } from "../../../domain/user";
import { IDocumentCollaborationParameterBean } from "./beans/IDocumentCollaborationParameterBean";
import { SortOrder } from "element-ui/types/table";

export class ObjectTranslator {
  constructor(protected _getToken: () => string) {
  }

  private _translatorRepository: TranslatorRepository = new TranslatorRepository(this._getToken);
  private _itemsAlternator: ItemsAlternator = new ItemsAlternator();
  private _formatTranslator: FormatObjectTranslator = new FormatObjectTranslator();
  private _hisInformationTranslator: HisInformationTranslator = new HisInformationTranslator();
  private _patientProfileInformationTranslator: PatientProfileInformationTranslator = new PatientProfileInformationTranslator();

  toDocument(getToken: () => string, data: any): Document {
    return TimeMeasure.log(
      "文書インスタンス生成",
      () => {
        this.assignPrivateIds(data);
        return this.beanToDocument(getToken, data);
      },
      "(ID:" + (data.documentId ? data.documentId : "") + ",FID:" + data.formatId + ")",
    );
  }

  /**
   * フロントエンド内の管理のため、ユニークなID(privateId)をアサインする.
   * ここの処理だけを見るとitemSetによらずdocument内でユニークなIDになるように見えるが、
   * 実際にはitemSet内でユニークなIDとなる。
   * 具体的には、文書編集中に続紙を追加したとき異なるitemSetで同じprivateIdが存在しうる。
   * なので、privateIdを扱うときにはどのitemSetのものなのかを意識する必要がある。
   */
  private assignPrivateIds(data: any): void {
    if (!data || !data.sections) {
      return;
    }
    let i = 0;
    for (const s of data.sections) {
      if (!s || !s.objects) {
        continue;
      }
      for (const o of s.objects) {
        if (!o) {
          continue;
        }
        o.privateId = ++i;
      }
    }
  }

  private beanToDocument(getToken: () => string, bean: IDocumentBean): Document {
    const sections = this.toSections(getToken, bean.documentId, bean.formatId, bean.sections);
    const itemSets = this.alternateItemSets(this.getObjectItemSets(bean.sections, bean.formatId, bean.documentId, bean.executionMode), sections);
    const extraFormats = this.toFormats(getToken, bean.joinableFormats);
    const hisInfo = this._hisInformationTranslator.toHisInformation(bean.hisInformation);
    const patientProfile = this._patientProfileInformationTranslator.toPatientProfileInformation(bean.patientProfileInformation);
    const bookmarks = this.toBookmarks(itemSets);
    // EditTokenとCreateTokenはフロントエンド上では明確に区別する
    const isEditToken = (bean.editToken && bean.editToken.isEditToken);
    return new Document(
      bean.documentId,
      bean.edition,
      this.toAttributes(bean),
      itemSets,
      sections,
      extraFormats,
      hisInfo,
      this.toReferenceFiles(bean.referenceFiles),
      patientProfile,
      DesignatedDoctorRepository.toDesignatedDoctorInformations(bean.designatedDoctorInformations),
      !!bean.isNewHisInformationApplied,
      this.toDocumentTokenInformation(isEditToken ? bean.editToken : null),
      this.toDocumentTokenInformation(isEditToken ? null : bean.editToken),
      this.toDocumentTokenInformation(bean.writeToken),
      null, // 施設選択ダイアログで選択したものが格納される
      null,
      bookmarks,
      Enumerable.from(bean.chainableFormats).select(f => f.originalId).toArray(),
      [], // フロントエンド内で使う一時的な情報.
      Enumerable.from(bean.chainedDocuments).select(d => new ChainedDocumentIdentifier(d.id, d.edition)).toArray(),
      [], // 登録時に設定するので空で初期化.
      false, // フロントエンド内で使う一時的な情報.(外部処理の実行を制限するかどうか)
      null, // フロントエンド内で使う一時的な情報.(外部処理の実行を制限するかどうか)
    );
  }

  public toReferenceFiles(beans: IReferenceFileInformationBean[]): ReferenceFileInformation[] {
    if (!beans) { return []; }
    return beans.map(bean => this.toReferenceFile(bean));
  }

  private toReferenceFile(bean: IReferenceFileInformationBean): ReferenceFileInformation {
    return new ReferenceFileInformation(bean.id, bean.name);
  }

  protected getObjectItemSets(sections: ISectionBean[], formatId: number, documentId: number, executionMode: string): ItemSet[] {
    const results: ItemSet[] = [];
    for (const section of sections) {
      if (!(section.groupId in results)) {
        results[section.groupId] = new ItemSet(formatId, []);
      }
      for (const item of section.objects) {
        const converted = this.toObjectItem(item, section.formatId, section.groupId, documentId, executionMode);
        results[section.groupId].items[converted.privateId] = converted;
      }
    }
    // ・ソース編集のソースを設定する.(Relationは同様にした方がいいが...実装済の所は手を出さない.)
    // ・チェックボックスオブジェクト/円選択オブジェクトと紐づいている固定オブジェクトにclickChainedObjectPrivateIdをセットする
    for (const result of results) {
      for (const item of result.items) {
        if (instanceOfISourceEditable(item)) {
          const relation = Enumerable.from(item.actions).firstOrDefault(a => a.kind === RelationalActionKinds.Alternative);
          if (!relation) {
            continue;
          }
          const source = Enumerable.from(result.items).firstOrDefault(i => !!i && (i.id === relation.objectId));
          if (!source) {
            continue;
          }
          item.source = source;
        }

        if (instanceOfIClickChainRequestable(item) && item.typeDetail === "Label") {
          const chainedItem = Enumerable.from(result.items).firstOrDefault(i => instanceOfIClickChainApplicable(i) && item.isClickChained(i.clickChainIdentifier));
          if (!chainedItem) { continue; }
          item.clickChainedObjectPrivateId = chainedItem.privateId;
        }
      }
    }
    return results;
  }

  public toObjectItem(bean: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const typeKey = this._translatorRepository.getTypeKeyFromBeanType(bean.type);
    const translator = this.getTranslator(typeKey);
    const result = translator.toItem(bean, formatId, groupId, documentId, executionMode);
    if (result) {
      this.setExtraMembers(result, bean);
    }
    return result;
  }

  protected getTranslator(typeKey: string) {
    return this.getTranslatorByType(typeKey);
  }

  protected getTranslatorByType(typeKey: string) {
    return this._translatorRepository.getTranslatorByTypeKey(typeKey);
  }

  protected setExtraMembers(instance: any, seed: any): void {
    // デバッグ用拡張.seedを設定.
    instance.seed = seed;
    instance.applyExtraMembers = (s: any, d: any) => {
      d.applyExtraMembers = s.applyExtraMembers;
      d.seed = s.seed;
    };
  }

  private alternateItemSets(itemSets: ItemSet[], sections: Section[]): ItemSet[] {
    return Enumerable.from(itemSets)
      .select((itemSet, index) => this.alternateItemSet(itemSet, sections.filter(s => s.itemSetId === index)))
      .toArray();
  }

  private alternateItemSet(itemSet: ItemSet, sections: Section[]): ItemSet {
    const cloned = itemSet.clone();
    cloned.items = this._itemsAlternator.alternate(cloned.items, sections);
    return cloned;
  }

  private toSections(getToken: () => string, documentId: number, formatId: number, sections: ISectionBean[]): Section[] {
    const results: Section[] = [];
    for (const section of sections) {
      const converted = this.toSection(getToken, documentId, formatId, section);
      if (converted) {
        results.push(converted);
      }
    }
    return results;
  }

  protected toSection(getToken: () => string, documentId: number, formatId: number, section: ISectionBean): Section {
    this.validateBodyRectangle(section);
    const itemPrivateIds = Array<number>();
    for (const object of section.objects) {
      itemPrivateIds.push((object as any).privateId);
    }
    return new Section(
      new Size(section.size.width, section.size.height),
      new Rectangle(
        new Point(
          section.bodyRectangle.topLeft.x,
          section.bodyRectangle.topLeft.y),
        new Size(
          section.bodyRectangle.size.width,
          section.bodyRectangle.size.height)),
      new LayerImageSource(getToken, section.formatId, section.formatSectionNumber.toString()),
      new Rectangle(
        new Point(
          section.layerImageOffset.x,
          section.layerImageOffset.y),
        new Size(
          section.layerImageSize.width,
          section.layerImageSize.height)),
      new SectionThumbnailImageSource(getToken, section.formatId, section.formatSectionNumber.toString()),
      section.groupId,
      itemPrivateIds,
      section.landscape,
      section.formatSectionNumber,
      section.formatId,
      section.pageKey,
      !!section.isPageSplittable,
      !!section.printOnSaving,
      false,
      section.splitRedundantMargin);
  }

  private validateBodyRectangle(section: ISectionBean): void {
    if (section.bodyRectangle.size.height < 0 || section.bodyRectangle.topLeft.y > section.size.height) {
      console.warn(section.size);
      console.warn(section.bodyRectangle);
      throw new Error("bodyRectangleが不正です。");
    }
  }

  protected toFormats(getToken: () => string, beans: IFormatBean[]): FormatReference[] {
    return this._formatTranslator.toFormats(getToken, beans);
  }

  private toAttributes(bean: IDocumentBean): DocumentAttributes {
    if (!bean) { return null; }
    return new DocumentAttributes(
      this.toPatient(bean.patient),
      this.toDepartment(bean.department),
      new FormatIdentifier(
        bean.formatId,
        bean.formatOriginalId,
        bean.formatMasterCode,
        bean.formatOriginalLayerId,
        bean.formatName,
        bean.reportTypeId,
      ),
      this.toDate(bean.createDateTime),
      bean.isTemporary,
      bean.isFixed,
      bean.note,
      bean.temporaryNote,
      null, // あとから格納される
      null, // あとから格納される
      bean.orderNo, // 新規記載の時はあとから格納される（編集時は格納されている）
      null, // あとから格納される
      null, // あとから格納される
      bean.hisInfoGetterParameter, // 新規記載の時はあとから格納される（編集時は格納されている）
      bean.shouldConfirmToSendCost,
      bean.shouldSendCost,
      bean.isDeleted,
      bean.defaultCopies,
      bean.indicationMagnification
        ? (bean.indicationMagnification / 100)
        : 1,
      bean.quotedDocumentId,
      bean.copiedDocumentId,
      bean.executionMode,
      bean.isExtendedPagePositionSelectable,
      null, // あとから格納する
      null, // あとから格納する
      this.toUser(bean.orderUserCode, bean.orderUserName),
      this.toUser(bean.attendingUserCode, bean.attendingUserName),
      bean.isRegisterableRegisteredDocumentAsTemporary,
      bean.isPrintableRegisteredDocumentAsTemporary,
      this.toDocumentButtonControl(bean.buttonControl),
      this.toDocumentEditToolControl(bean.editToolControl),
      bean.shouldSendToHis,
      null, // あとから格納する（将来的にはbeanから取るようにしたい）
      false,
      bean.isVirtual,
      bean.registerAlertType,
      bean.quoteBehavior,
      this.toExternalComments(bean.externalComments),
      this.toDocumentCollaborationSupplement(bean.collaborationSupplement),
      null, // あとから格納される
      this.toDocumentCollaborationBehavior(bean.collaborationBehavior),
      EditRuleSetTranslator.toEditRuleSet(bean.editRuleSet),
      new DocumentEditOperation(),
    );
  }

  private toDocumentCollaborationBehavior(bean: IDocumentCollaborationBehaviorBean): DocumentCollaborationBehavior {
    if (!bean) { return null; }
    const detail = new DocumentCollaborationBehavior();
    detail.isManageable = bean.isManageable;
    return detail;
  }

  /**
   * バックエンド側は複数持てるように対応しているが、現状フロントエンドはそこまでする必要が無いので持ち方に差異がある
   */
  private toDocumentCollaborationSupplement(bean: IDocumentCollaborationSupplementBean): DocumentCollaborationSupplement {
    if (!bean || !bean.actionRequestedUsers || bean.actionRequestedUsers.length === 0) { return null; }
    const actionRequestedUser = bean.actionRequestedUsers[0];
    const supplement = new DocumentCollaborationSupplement();
    supplement.actionRequestedUserCode = actionRequestedUser.userCode;
    supplement.actionRequestedUserName = actionRequestedUser.userName;
    return supplement;
  }

  private toExternalComments(beans: IExternalCommentBean[]): ExternalComment[] {
    if (!beans) { return []; }
    return beans.map(b => this.toExternalComment(b));
  }

  private toExternalComment(bean: IExternalCommentBean): ExternalComment {
    const comment = new ExternalComment();
    comment.comment = bean.comment;
    comment.updateUserName = bean.updateUserName;
    comment.updateDateTime = this.toDate(bean.updateDateTime);
    return Object.freeze(comment);
  }

  toUser(userCode: string, userName: string): User {
    if (!userCode) { return null; }
    return new User(userCode, userName, null, null, null, null);
  }

  private toDocumentButtonControl(bean: IDocumentButtonControl): DocumentButtonControl {
    if (!bean) { return null; }
    const result = new DocumentButtonControl();
    result.isRegisterEnable = bean.isRegisterEnable;
    result.isRegisterAndPrintEnable = bean.isRegisterAndPrintEnable;
    result.isRegisterAndPrintWithDialogEnable = bean.isRegisterAndPrintWithDialogEnable;
    result.isRegisterAsTemporaryEnable = bean.isRegisterAsTemporaryEnable;
    // TODO: 一時保存印刷（選択）ボタンの追加
    result.isRegisterAsTemporaryAndPrintEnable = bean.isRegisterAsTemporaryAndPrintEnable || bean.isRegisterAsTemporaryAndPrintWithDialogEnable;
    result.isPrintEnable = bean.isPrintEnable;
    result.isPrintWithDialogEnable = bean.isPrintWithDialogEnable;
    return result;
  }

  private toDocumentEditToolControl(bean: IDocumentEditToolControl): DocumentEditToolControl {
    if (!bean) { return null; }
    const result = new DocumentEditToolControl();
    result.isNewTextObjectCreatable = bean.isNewTextObjectCreatable;
    result.isNewImageObjectCreatable = bean.isNewImageObjectCreatable;
    result.isNewLineObjectCreatable = bean.isNewLineObjectCreatable;
    result.isNewShapeObjectCreatable = bean.isNewShapeObjectCreatable;
    result.isObjectStyleEditable = bean.isObjectStyleEditable;
    return result;
  }

  private toPatient(bean: IPatientBean): Patient {
    return bean
      ? new Patient(
        bean.patientId,
        bean.name,
        bean.kanaName,
        bean.birthDate ? Moment(bean.birthDate.replace(/\//g, "")).toDate() : null, // TODO: なぜかここだけYYYY/MM/DD.同じ型のデータは同じフォーマットにしたい.
        this.toSex(bean.sex)
      )
      : null;
  }

  private toSex(source: number): Sex {
    switch (source) {
      case 1:
        return Sex.Male;
      case 2:
        return Sex.Female;
      default:
        return Sex.Unknown;
    }
  }

  public toDepartment(bean: IDepartmentBean): Department {
    if (!bean) { return null; }
    return new Department(
      bean.departmentId,
      bean.name,
      bean.shortName
    );
  }

  private toDate(dateTimeAsString: string): Date {
    if (!dateTimeAsString) {
      return new Date();
    }
    const parsed = Moment(dateTimeAsString, this.dateTimeFormat).toDate();
    return !parsed
      ? new Date()
      : parsed;
  }

  private toDocumentTokenInformation(bean: IDocumentTokenInformationBean) {
    if (!bean) { return null; }
    return new DocumentTokenInformation (
      bean.token,
      bean.tokenLifeSpan,
      this.toCurrentEditors(bean.editors),
    );
  }

  public toCurrentEditors(beans: ICurrentEditorBean[]): CurrentEditors[] {
    if (!beans) { return []; }
    const results: CurrentEditors[] = [];
    for (const bean of beans) {
      results.push(this.toCurrentEditor(bean));
    }
    return results;
  }

  private toCurrentEditor(bean: ICurrentEditorBean): CurrentEditors {
    if (!bean) { return null; }
    return new CurrentEditors (
      bean.userName,
      bean.terminalName,
      this.toDate(bean.editStartDateTime),
      bean.token,
    );
  }
  
  private readonly dateTimeFormat = "YYYYMMDDHHmmss";

  toSaveDocumentBean(document: Document): IDocumentBean {
    return {
      formatId: document.attributes.format.id,  // TODO:文書につきformatIdがひとつとは限らない。sectionにもformatIdあり。
      formatOriginalId: document.attributes.format.originalId,
      formatMasterCode: document.attributes.format.masterCode,
      formatOriginalLayerId: document.attributes.format.originalLayerId,
      formatName: document.attributes.format.name,
      reportTypeId: document.attributes.format.reportTypeId,
      documentId: document.documentId,
      sections: this.toSectionBeans(document.sections, document.itemSets),
      createDateTime: Moment(document.attributes.createdDate).format(this.dateTimeFormat),
      patient: this.toPatientBean(document.attributes.patient),
      department: this.toDepartmentBean(document.attributes.department),
      joinableFormats: this.toFormatsBean(document.extraFormats),
      referenceFiles: document.referenceFiles,
      isTemporary: document.attributes.isTemporary,
      isFixed: document.attributes.isFixed,
      note: document.attributes.note,
      temporaryNote: document.attributes.temporaryNote,
      systemKey: document.attributes.systemKey,
      linkKey: document.attributes.linkKey,
      orderNo: document.attributes.orderNo,
      institutionNo: document.attributes.institutionNo,
      regionalInstitutionNo: document.attributes.regionalInstitutionNo,
      hisInfoGetterParameter: document.attributes.hisInfoGetterParameter,
      hisInformation: this.toHisInformationBean(document.hisInformation),
      patientProfileInformation: this.toPatientProfileInformationBean(document.patientProfile),
      designatedDoctorInformations: [],  // 現状では返却する必要なし
      isNewHisInformationApplied: document.isNewHisInformationApplied,
      shouldConfirmToSendCost: document.attributes.shouldConfirmToSendCost,
      shouldSendCost: document.attributes.shouldSendCost,
      isDeleted: document.attributes.isDeleted,
      defaultCopies: document.attributes.defaultCopies,
      indicationMagnification: Math.round(document.attributes.defaultScale * 100),
      quotedDocumentId: document.attributes.quotedDocumentId,
      copiedDocumentId: document.attributes.copiedDocumentId,
      executionMode: document.attributes.executionMode,
      isExtendedPagePositionSelectable: document.attributes.isExtendedPagePositionSelectable,
      edition: document.edition,
      editToken: this.toDocumentTokenInformationBean(document.editToken ? document.editToken : document.createToken, !!document.editToken),
      writeToken: this.toDocumentTokenInformationBean(document.writeToken, false),
      selectedInstitution: this.toInstitutionBean(document.selectedInstitution),
      orderUserCode: document.attributes.orderUser ? document.attributes.orderUser.id : null,
      orderUserName: document.attributes.orderUser ? document.attributes.orderUser.name : null,
      attendingUserCode: document.attributes.attendingUser ? document.attributes.attendingUser.id : null,
      attendingUserName: document.attributes.attendingUser ? document.attributes.attendingUser.name : null,
      isRegisterableRegisteredDocumentAsTemporary: document.attributes.isRegisterableRegisteredDocumentAsTemporary === undefined || document.attributes.isRegisterableRegisteredDocumentAsTemporary,
      isPrintableRegisteredDocumentAsTemporary: document.attributes.isPrintableRegisteredDocumentAsTemporary === undefined || document.attributes.isPrintableRegisteredDocumentAsTemporary,
      buttonControl: null,    // 現状では返却する必要なし
      editToolControl: null,  // 現状では返却する必要なし
      shouldSendToHis: document.attributes.shouldSendToHis,
      chainableFormats: Enumerable.from(document.chainableFormatOriginalIds).select(i => ({
        originalId: i,
      })).toArray(),
      chainedDocuments: Enumerable.from(document.chainedDocuments).select(d => ({
        id: d.id,
        edition: d.edition,
      })).toArray(),
      chainDocuments: Enumerable.from(document.chainDocuments).select(d => this.toSaveDocumentBean(d)).toArray(),
      ignoreFixKey: document.attributes.ignoreFixKey,
      isDocumentNoteEdited: document.attributes.isDocumentNoteEdited,
      isVirtual: document.attributes.isVirtual,
      registerAlertType: document.attributes.registerAlertKind,
      quoteBehavior: document.attributes.quoteBehaviorKind,
      externalComments: null,   // 現状では返却する必要なし
      collaborationSupplement: this.toDocumentCollaborationSupplementBean(document.attributes.collaborationSupplement),
      collaborationParameter: this.toDocumentCollaborationParameterBean(document.attributes.parameterContainer),
      collaborationBehavior: null,  // 現状では返却する必要なし
      editRuleSet: null,  // 現状では返却する必要なし
    };
  }

  /**
   * バックエンド側は複数持てるように対応しているが、現状フロントエンドはそこまでする必要が無いので持ち方に差異がある
   */
  private toDocumentCollaborationSupplementBean(supplement: DocumentCollaborationSupplement): IDocumentCollaborationSupplementBean {
    if (!supplement || !supplement.actionRequestedUserCode) { return null; }

    return {
      actionRequestedUsers: [
        {
          userCode: supplement.actionRequestedUserCode,
          userName: supplement.actionRequestedUserName,
        }
      ]
    };
  }

  private toDocumentCollaborationParameterBean(container: DocumentParameterContainer): IDocumentCollaborationParameterBean {
    if (!container || !container.collaborationParameter) { return null; }

    return {
      problemNumber: container.collaborationParameter.problemNo,
      orderUserCode: container.collaborationParameter.orderUserCode,
    };
  }

  private toInstitutionBean(institution: InstitutionItem): IInstitutionBean {
    if (!institution) { return null; }
    const translator = new InstitutionInformationTranslator();
    return translator.toInstitutionBean(institution.value);
  }

  private toPatientBean(source: Patient): IPatientBean {
    return {
      patientId: source.id,
      name: source.name,
      kanaName: source.kanaName,
      birthDate: Moment(source.birthday).format("YYYY/MM/DD"),
      sex: this.fromSex(source.sex),
    };
  }

  private fromSex(source: Sex): number {
    return source;
  }

  private toDepartmentBean(source: Department): IDepartmentBean {
    if (!source) { return null; }
    return {
      departmentId: source.id,
      name: source.name,
      shortName: source.shortName,
      isDeleted: source.isDeleted,
    };
  }

  private toFormatsBean(formats: FormatReference[]): IFormatBean[] {
    return Enumerable.from(formats)
      .select(this.toFormatBean)
      .toArray();
  }

  private toFormatBean(source: FormatReference): IFormatBean {
    return {
      id: source.id,
      name: source.name,
      reportTypeId: source.reportTypeId,
      masterCode: source.masterCode,
      tag: source.tag,
      companyCode: source.company ? source.company.id : null,
      companyName: source.company ? source.company.name : null,
      categoryType: source.categoryType,
      writeMethodType: source.writeMethodType,
      startDateTime: source.startDateTime ? source.startDateTime : "",
      endDateTime: source.endDateTime ? source.endDateTime : "",
      expired: source.expired,
      deleted: source.deleted,
      editedName: source.editedName, // 文書編集ではフォーマット名を編集することはないのでそのまま返却する
      note: null,
      quoteBehavior: source.quoteBehaviorKind,
    };
  }

  private toSectionBeans(sections: Section[], itemSets: ItemSet[]): ISectionBean[] {
    const unalternatedItemSets = this.unalternateItemSets(itemSets);
    const results: ISectionBean[] = [];
    for (const section of sections) {
      results.push(this.toSectionBean(section, unalternatedItemSets[section.itemSetId].items));
    }
    return results;
  }

  private unalternateItemSets(itemSets: ItemSet[]): ItemSet[] {
    return Enumerable.from(itemSets)
      .select(i => this.unalternateItemSet(i))
      .toArray();
  }

  private unalternateItemSet(itemSet: ItemSet): ItemSet {
    const cloned = itemSet.clone();
    cloned.items = this._itemsAlternator.unalternate(cloned.items);
    return cloned;
  }

  private toSectionBean(section: Section, items: IItem[]): ISectionBean {
    return {
      size: {
        width: section.size.width,
        height: section.size.height,
      },
      bodyRectangle: {
        topLeft: {
          x: section.bodyRectangle.x,
          y: section.bodyRectangle.y,
        },
        size: {
          width: section.bodyRectangle.width,
          height: section.bodyRectangle.height,
        },
      },
      layerImageOffset: null,  // TODO:implement
      layerImageSize: null,    // TODO:implement
      objects: this.toItemBeansFromItems(section.itemPrivateIds, items),
      landscape: section.landscape,
      formatSectionNumber: section.formatSectionNumber,
      groupId: section.itemSetId,
      formatId: section.formatId,
      pageKey: section.pageKey,
      isPageSplittable: section.isPageSplittable,
      printOnSaving: section.shouldPrintOnFirstRegistered,
      splitRedundantMargin: section.splitRedundantMargin,
    };
  }

  private toItemBeansFromItems(privateIds: number[], items: IItem[]): ITypeDefinable[] {
    const results: ITypeDefinable[] = [];
    for (const privateId of privateIds) {
      const bean = this.toItemBeanFromItems(privateId, items);
      if (!bean) { continue; }
      results.push(bean);
    }
    return results;
  }

  private toItemBeanFromItems(privateId: number, items: IItem[]): ITypeDefinable {
    for (const item of items) {
      // index指定でいける？
      if (!item) { continue; }
      if (item.privateId !== privateId) { continue; }

      return this.toItemBean(item);
    }
    return null;
  }

  public toItemBean(item: IItem): ITypeDefinable {
    const translator = this._translatorRepository.getTranslatorByTypeKey(item.typeKey);
    return translator.toBean(item);
  }

  private toHisInformationBean(hisInformation: HisInformation): IHisInformationBean {
    return this._hisInformationTranslator.toHisInformationBean(hisInformation);
  }

  private toPatientProfileInformationBean(info: PatientProfileInformation): IPatientProfileInformationBean {
    return this._patientProfileInformationTranslator.toPatientProfileInformationBean(info);
  }

  private toDocumentTokenInformationBean(token: DocumentTokenInformation, isEditToken: boolean): IDocumentTokenInformationBean {
    if (!token) { return null; }
    return {
      token: token.token,
      tokenLifeSpan: token.tokenLifeSpan,
      editors: this.toCurrentEditorBeans(token.editors),
      isEditToken: isEditToken,
    };
  }

  private toCurrentEditorBeans(sources: CurrentEditors[]): ICurrentEditorBean[] {
    if (!sources) { return null; }
    const results: ICurrentEditorBean[] = [];
    for (const source of sources) {
      results.push(this.toCurrentEditorBean(source));
    }
    return results;
  }

  private toCurrentEditorBean(source: CurrentEditors): ICurrentEditorBean {
    if (!source) { return; }
    return {
      userName: source.userName,
      terminalName: source.terminalName,
      editStartDateTime: Moment(source.editStartDateTime).format(this.dateTimeFormat),
      token: source.token,
    };
  }

  public toNewDocumentRequest(source: INewDocumentRequestBean): INewDocumentRequest {
    return {
      formatOriginalId: source.formatOriginalId,
      patientNo: source.patientCode,
      departmentCode: source.departmentCode,
      isTemporary: source.isTemporary,
      isDeleted: source.isDeleted,
      userCode: source.userCode,
      createdDateTime: source.createdDateTime,
      lastUpdatedDateTime: source.lastUpdatedDateTime,
      systemKey: source.systemKey,
      linkKey: source.linkKey,
      institutionNo: source.institutionNo,
      regionalInstitutionNo: source.regionalInstitutionNo,
      hisInfoGetterParameter: source.hisInfoGetterParameter,
      shouldValidateExistingLinkedDocument: source.shouldValidateExistingLinkedDocument,
      shouldOverwriteExistingLinkedDocument: source.shouldOverwriteExistingLinkedDocument,
      objects: Enumerable.from(source.objects).select(o => new RequestedObject(o.id, o.key, o.value)).toArray(),
    };
  }

  public toNewDocumentAlternativeRequest(source: INewDocumentAlternativeRequestBean): INewDocumentAlternativeRequest {
    const request = this.toNewDocumentRequest(source) as INewDocumentAlternativeRequest;
    request.url = source.url;
    return request;
  }

  public toDocumentObjectsInformation(bean: IDocumentObjectsInformationBean): DocumentObjectsInformation {
    if (!bean) { return null; }
    const document = new DocumentObjectsInformation();
    document.sections = Enumerable.from(bean.sections).select(b => this.toDocumentObjectsInformationSection(b)).toArray();
    return document;
  }

  private toDocumentObjectsInformationSection(bean: IDocumentObjectsInformationSectionBean): DocumentObjectsInformationSection {
    if (!bean) { return null; }
    const section = new DocumentObjectsInformationSection();
    section.objects = Enumerable.from(bean.objects).select(o => this.toDocumentObjectsInformationObject(o)).toArray();
    return section;
  }

  private toDocumentObjectsInformationObject(bean: IDocumentObjectsInformationObjectBean): DocumentObjectsInformationObject {
    if (!bean) { return null; }
    const object = new DocumentObjectsInformationObject();
    object.type = bean.type;
    object.typeDetail = bean.typeDetail;
    object.id = bean.id;
    object.name = bean.name;
    object.subName = bean.subName;
    object.viewTitle = bean.viewTitle;
    object.itemName = bean.itemName;
    object.groupName = bean.groupName;
    object.value = bean.value;
    object.valueFormat = bean.valueFormat;
    object.text = bean.text;
    object.isSelected = bean.isSelected;
    object.hisItemName = bean.hisItemName;
    object.isTime = bean.isTime;
    object.isEmpty = bean.isEmpty;
    object.dateTimeFormat = bean.dateTimeFormat;
    object.dateTimeLanguage = bean.dateTimeLanguage;
    object.options = bean.options
      ? Enumerable.from(bean.options).select(o => this.toDocumentObjectsInformationObjectOption(o)).toArray()
      : undefined;
    return object;
  }

  private toDocumentObjectsInformationObjectOption(bean: IDocumentObjectsInformationObjectOptionBean): DocumentObjectsInformationObjectOption {
    if (!bean) { return null; }
    const option = new DocumentObjectsInformationObjectOption();
    option.text = bean.text;
    option.value = bean.value;
    return option;
  }

  private toBookmarks(itemSets: ItemSet[]): IBookmark[] {
    return Enumerable.from(itemSets)
      .selectMany((s, i) => Enumerable.from(s.items).select(x => this.toBookmark(i, x)).where(x => !!x))
      .toArray();
  }

  private toBookmark(itemSetId: number, item: IItem): IBookmark {
    if (!(item instanceof AbstractItem)) { return null ; }
    const action = item.actions.find(a => a.kind === RelationalActionKinds.Bookmark);
    return action
      ? {
        itemSetId: itemSetId,
        item: item,
        name: action.value,
        row: action.row,
        column: action.column,
      }
      : null;
  }

  public toDocumentSearchPresetConditionBean(source: DocumentSearchPresetCondition): IDocumentSearchPresetConditionBean {
    // 日付関連の条件は保存しないので削除
    source.condition.fromDate = null;
    source.condition.toDate = null;
    return {
      key: source.key,
      name: source.name,
      condition: this.toDocumentSearchParameterBean(source.condition),
      presetKind: source.presetKind,
      index: source.index,
      departmentId: source.departmentId,
      isDefaultSelected: source.isDefaultSelected,
    };
  }

  private toDocumentSearchParameterBean(source: DocumentSearchParameter): IDocumentSearchParameterBean {
    return {
      patientId: source.patientId,
      startDate: source.fromDate,
      endDate: source.toDate,
      durationName: source.durationName,
      departmentGroupId: source.departmentGroupId,
      departmentIds: source.departmentIds,
      departmentId: source.departmentId,
      containsDeleted: source.containsDeleted,
      containsTemporary: source.containsTemporary,
      containsRegistered: source.containsRegistered,
      documentKindIds: source.documentKindIds,
      loginUserId: source.loginUserId,
      contentDateSearch: source.contentDateSearch,
      loginUserOnly: source.loginUserOnly,
      formatName: source.formatName,
      formatIds: source.formatIds,
      manageNumberText: source.manageNumberText,
      containsVirtualDocuments: source.containsVirtualDocuments,
      updateUserCodes: source.updateUserCodes,
    };
  }

  public toPresetSearchConditions(beans: IDocumentSearchPresetConditionBean[]): DocumentSearchPresetCondition[] {
    return Enumerable.from(beans).select(b => this.toPresetSearchCondition(b)).toArray();
  }

  private toPresetSearchCondition(bean: IDocumentSearchPresetConditionBean): DocumentSearchPresetCondition {
    const condition = new DocumentSearchPresetCondition();
    condition.key = bean.key;
    condition.name = bean.name;
    condition.condition = this.toDocumentSearchParameter(bean.condition);
    condition.presetKind = bean.presetKind;
    condition.index = bean.index;
    condition.departmentId = bean.departmentId;
    condition.isDefaultSelected = bean.isDefaultSelected;
    return condition;
  }

  private toDocumentSearchParameter(bean: IDocumentSearchParameterBean): DocumentSearchParameter {
    const documentKindIds = bean.documentKindIds ? bean.documentKindIds.concat() : [];
    if (bean.documentKindId && documentKindIds.indexOf(bean.documentKindId) < 0) {
      documentKindIds.push(bean.documentKindId);
    }
    const parameter = new DocumentSearchParameter();
    parameter.patientId = bean.patientId;
    parameter.fromDate = bean.startDate;
    parameter.toDate = bean.endDate;
    parameter.durationName = bean.durationName;
    parameter.departmentGroupId = bean.departmentGroupId;
    parameter.departmentIds = bean.departmentIds;
    parameter.departmentId = bean.departmentId;
    parameter.containsDeleted = bean.containsDeleted;
    parameter.containsTemporary = bean.containsTemporary;
    parameter.containsRegistered = bean.containsRegistered;
    parameter.documentKindIds = documentKindIds;
    parameter.loginUserId = bean.loginUserId;
    parameter.contentDateSearch = bean.contentDateSearch;
    parameter.loginUserOnly = bean.loginUserOnly;
    parameter.formatName = bean.formatName;
    parameter.formatIds = bean.formatIds;
    parameter.manageNumberText = bean.manageNumberText;
    parameter.containsVirtualDocuments = bean.containsVirtualDocuments;
    parameter.updateUserCodes = bean.updateUserCodes;
    return parameter;
  }

  public toDocumentSearchPresetConditionOrderBeans(beans: string[]): IDocumentSearchPresetConditionOrderBean[] {
    return Enumerable.from(beans).select(b => this.toDocumentSearchPresetConditionOrderBean(b)).toArray();
  }

  private toDocumentSearchPresetConditionOrderBean(bean: string): IDocumentSearchPresetConditionOrderBean {
    return {
      key: bean,
    };
  }

  public toDocumentSearchPresetConditionKeys(beans: IDocumentSearchPresetConditionOrderBean[]): string[] {
    return Enumerable.from(beans).select(bean => this.toDocumentSearchPresetConditionKey(bean)).toArray();
  }

  private toDocumentSearchPresetConditionKey(bean: IDocumentSearchPresetConditionOrderBean): string {
    return bean ? bean.key : null;
  }

  public toDocumentPublishedHistory(beans: IDocumentPublishedBean[]): IDocumentPublished[] {
    return Enumerable.from(beans).select(b => this.toDocumentPublished(b)).toArray();
  }

  public toDocumentPublished(bean: IDocumentPublishedBean): IDocumentPublished {
    return {
      edition: bean.edition,
      publishedCopies: bean.publishedCopies,
      publishedDateTime: this.toDate(bean.publishedDateTime),
      publishedUserName: bean.publishedUserName,
      publishedTerminal: bean.publishedTerminal,
      publishedPrinterName: bean.publishedPrinterName,
    };
  }

  public toPresetSortConditionBeans(models: DocumentListPresetSortCondition[]): IDocumentListPresetSortConditionBean[] {
    return Enumerable.from(models).select(model => this.toPresetSortConditionBean(model)).toArray();
  }

  private toPresetSortConditionBean(model: DocumentListPresetSortCondition): IDocumentListPresetSortConditionBean {
    return {
      label: model.label,
      items: this.toPresetSortConditionItemBeans(model.items),
      isDefault: model.isDefault,
      isCommon: model.isCommon,
    };
  }

  private toPresetSortConditionItemBeans(models: DocumentListPresetSortConditionItem[]): IDocumentListPresetSortConditionItemItemBean[] {
    return Enumerable.from(models).select(model => this.toPresetSortConditionItemBean(model)).toArray();
  }

  private toPresetSortConditionItemBean(model: DocumentListPresetSortConditionItem): IDocumentListPresetSortConditionItemItemBean {
    return {
      sortType: model.sortType,
      orderType: model.orderType,
    };
  }

  public toDocumentListPresetSortConditions(beans: IDocumentListPresetSortConditionBean[]): DocumentListPresetSortCondition[] {
    return Enumerable.from(beans).select(bean => this.toDocumentListPresetSortCondition(bean)).toArray();
  }

  private toDocumentListPresetSortCondition(bean: IDocumentListPresetSortConditionBean): DocumentListPresetSortCondition {
    if (!bean) { return null; }

    const model = new DocumentListPresetSortCondition();
    model.label = bean.label;
    model.items = this.toDocumentListPresetSortConditionItems(bean.items);
    model.isDefault = bean.isDefault;
    model.isCommon = bean.isCommon;
    return model;
  }

  private toDocumentListPresetSortConditionItems(beans: IDocumentListPresetSortConditionItemItemBean[]): DocumentListPresetSortConditionItem[] {
    return Enumerable.from(beans).select(bean => this.toDocumentListPresetSortConditionItem(bean)).toArray();
  }

  private toDocumentListPresetSortConditionItem(bean: IDocumentListPresetSortConditionItemItemBean): DocumentListPresetSortConditionItem {
    if (!bean) { return null; }

    const model = new DocumentListPresetSortConditionItem();
    model.sortType = bean.sortType;
    model.orderType = bean.orderType as SortOrder;
    return model;
  }

  public toDocumentInjectedData(bean?: IDocumentInjectedDataBean): DocumentInjectedData | null {
    return new DocumentInjectedDataTranslator().to(bean);
  }
}

class DocumentInjectedDataTranslator {
  to(bean?: IDocumentInjectedDataBean): DocumentInjectedData | null {
    return bean
      ? new DocumentInjectedData(this.toObjects(bean.objects), this.toEvidences(bean.evidences))
      : null;
  }

  private toObjects(bean?: IDocumentInjectedObjectBean[]): DocumentInjectedObject[] {
    return this.toArray(bean).filter(b => !!b).map(b => this.toObject(b));
  }

  private toObject(bean: IDocumentInjectedObjectBean): DocumentInjectedObject {
    return new DocumentInjectedObject(
      this.orNull(bean.id),
      this.orNull(bean.key),
      this.orEmpty(bean.value),
      this.orNull(bean.label),
      this.toArray(bean.grounds).filter(b => !!b).map(b => this.toGround(b)),
    );
  }

  private toGround(bean: IDocumentInjectedGroundBean): DocumentInjectedGround {
    return new DocumentInjectedGround(
      this.orNull(bean.reason),
      this.orNull(bean.evidenceKey),
      !!bean.isReliable,
    );
  }

  private toEvidences(bean?: IDocumentInjectedEvidenceBean[]): DocumentInjectedEvidence[] {
    return this.toArray(bean).filter(b => !!b).map(b => this.toEvidence(b)).filter(e => !!e);
  }

  private toEvidence(bean: IDocumentInjectedEvidenceBean): DocumentInjectedEvidence | null {
    return Object.keys(this.evidenceFactories).includes(bean.type)
      ? this.evidenceFactories[bean.type](bean)
      : null;
  }

  private evidenceFactories: { [key in DocumentInjectedContentType] : (b: IDocumentInjectedEvidenceBean) => DocumentInjectedEvidence } = {
    text: b => new DocumentInjectedEvidenceText(
      this.orEmpty(b.key),
      this.orEmpty(b.content),
      !!b.isReliable,
      this.orNull(b.name),
      this.orNull(b.description),
      this.toDateTimeOrNull(b.createdDateTime),
      this.orNull(b.department),
      this.toAuthor(b.author),
    ),
    html: b => new DocumentInjectedEvidenceHtml(
      this.orEmpty(b.key),
      this.orEmpty(b.content),
      !!b.isReliable,
      this.orNull(b.name),
      this.orNull(b.description),
      this.toDateTimeOrNull(b.createdDateTime),
      this.orNull(b.department),
      this.toAuthor(b.author),
    ),
    url: b => new DocumentInjectedEvidenceUrl(
      this.orEmpty(b.key),
      this.orEmpty(b.content),
      !!b.isReliable,
      this.orNull(b.name),
      this.orNull(b.description),
      this.toDateTimeOrNull(b.createdDateTime),
      this.orNull(b.department),
      this.toAuthor(b.author),
    ),
  };

  private toAuthor(bean?: IDocumentInjectedEvidenceAuthorBean): DocumentInjectedEvidenceAuthor | null {
    return bean
      ? new DocumentInjectedEvidenceAuthor(
        this.orEmpty(bean.name),
        this.orNull(bean.role),
      )
      : null;
  }

  private orNull(bean?: string): string | null {
    return this.orDefault(bean, null);
  }

  private orEmpty(bean?: string): string {
    return this.orDefault(bean, "");
  }

  private orDefault<T>(bean: T | undefined, defaultValue: T): T {
    return bean ? bean : defaultValue;
  }

  private toArray<T>(bean?: T[]): T[] {
    return bean ? bean : [];
  }

  private toDateTimeOrNull(bean?: string): Date | null {
    if (!bean || bean.length < 14) {
      return null;
    }
    const result = Moment(bean.slice(0, 8) + "T" + bean.slice(8)).toDate();
    return Number.isNaN(result.getTime())
      ? null
      : result;
  }
}
